import React, { createContext, useContext, useMemo } from 'react';
import { useEditorRef, useEditorSelector, usePluginOptions } from 'platejs/react';
import { TablePlugin } from '@platejs/table/react';
import { getSelectedCells } from '@platejs/table';
import { CELL_SELECTION_OPTION, getCellPathKey, getEditingCellPathKey, getSelectedCell } from './cellSelection';

/**
 * Per-table activity for cell chrome (editing cursor, Excel select, multi-select).
 *
 * Every mounted <td> used to call useEditorSelector / useTableCellElement /
 * usePluginOptions itself. On a CAM (~80 tables, ~4k cells) that is thousands
 * of jotai subscribers running on every keystroke — even when React.memo
 * skipped the paint. One provider per table holds path-stable keys so typing
 * inside a cell does not change the context value (path is unchanged; only
 * the node object is new).
 */
export interface TableCellActivity {
    editingPathKey: string | null;
    singleSelectedPathKey: string | null;
    librarySelectedKeys: string;
}

const TableCellActivityContext = createContext<TableCellActivity>({
    editingPathKey: null,
    singleSelectedPathKey: null,
    librarySelectedKeys: '',
});

function librarySelectedPathKeyString(editor: any): string {
    try {
        const cells = (getSelectedCells as any)(editor) as any[] | null;
        if (!cells || cells.length < 2) return '';
        const keys = cells.map((c) => getCellPathKey(editor, c)).filter(Boolean) as string[];
        if (keys.length < 2) return '';
        return `|${keys.join('|')}|`;
    } catch {
        return '';
    }
}

export function TableCellActivityProvider({ children }: { children: React.ReactNode }) {
    const editor = useEditorRef();
    const editingPathKey = (useEditorSelector as any)((ed: any) => getEditingCellPathKey(ed), []) as string | null;
    const singleSelectedNode = (usePluginOptions as any)(TablePlugin, (state: any) => state?.[CELL_SELECTION_OPTION] ?? null) ?? null;
    const singleSelectedPathKey = getCellPathKey(editor, singleSelectedNode) ?? getCellPathKey(editor, getSelectedCell(editor));
    const librarySelectedKeys = (useEditorSelector as any)((ed: any) => librarySelectedPathKeyString(ed), []) as string;

    const value = useMemo<TableCellActivity>(
        () => ({ editingPathKey, singleSelectedPathKey, librarySelectedKeys }),
        [editingPathKey, singleSelectedPathKey, librarySelectedKeys],
    );

    return <TableCellActivityContext.Provider value={value}>{children}</TableCellActivityContext.Provider>;
}

export function useTableCellActivity(): TableCellActivity {
    return useContext(TableCellActivityContext);
}

export function cellMatchesActivity(
    pathKey: string | null,
    activity: TableCellActivity,
): { isEditing: boolean; selected: boolean; singleSelected: boolean } {
    if (!pathKey) return { isEditing: false, selected: false, singleSelected: false };
    const isEditing = activity.editingPathKey === pathKey;
    const singleSelected = activity.singleSelectedPathKey === pathKey;
    const librarySelected = activity.librarySelectedKeys.length > 0 && activity.librarySelectedKeys.includes(`|${pathKey}|`);
    return { isEditing, selected: singleSelected || librarySelected, singleSelected };
}
